import { useAllHelpRequests } from "@/hooks/useAllHelpRequests";
import { UserName } from "@/components/badges/UserName";
import { useUpdateHelpRequest, HelpRequest } from "@/hooks/useHelpRequests";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Hand, Clock, CheckCircle, MessageCircle, Lock, Eye, BookOpen, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export function HelpRequestsList() {
  const { user } = useAuth();
  const { data: requests, isLoading } = useAllHelpRequests();
  const updateRequest = useUpdateHelpRequest();

  const handleResolve = async (request: HelpRequest & { course?: { id: string; title: string } }) => {
    if (!user) return;

    try {
      await updateRequest.mutateAsync({
        id: request.id,
        course_id: request.course_id,
        status: "resolved",
        resolved_by: user.id,
      });
      toast.success("Help request marked as resolved");
    } catch {
      toast.error("Failed to update request");
    }
  };

  const getStatusBadge = (status: HelpRequest["status"]) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> Pending</Badge>;
      case "in_progress":
        return <Badge variant="default" className="gap-1"><MessageCircle className="h-3 w-3" /> In Progress</Badge>;
      case "resolved":
        return <Badge variant="outline" className="gap-1 text-green-600 border-green-600"><CheckCircle className="h-3 w-3" /> Resolved</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!requests || requests.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Hand className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No help requests to review</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {requests.map((request) => (
        <Card key={request.id} className={request.status === "resolved" ? "opacity-70" : ""}>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Avatar className="h-10 w-10 flex-shrink-0">
                  <AvatarImage src={request.author?.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {request.author?.full_name?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium"><UserName userId={request.author?.id} name={request.author?.full_name} fallback="Unknown" /></span>
                    {getStatusBadge(request.status)}
                    {request.is_private && (
                      <Badge variant="outline" className="gap-1 text-xs">
                        <Lock className="h-3 w-3" /> Private
                      </Badge>
                    )}
                    {!request.is_private && (
                      <Badge variant="outline" className="gap-1 text-xs text-muted-foreground">
                        <Eye className="h-3 w-3" /> Public
                      </Badge>
                    )}
                  </div>
                  
                  {request.course && (
                    <Link 
                      to={`/courses/${request.course.id}`}
                      className="flex items-center gap-1 text-sm text-primary hover:underline mt-1"
                    >
                      <BookOpen className="h-3 w-3" />
                      {request.course.title}
                    </Link>
                  )}
                  
                  <h4 className="font-medium mt-2">{request.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-3">
                    {request.content}
                  </p>
                  
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span>{formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}</span>
                    {request.resolver && (
                      <span>Resolved by <UserName userId={request.resolver.id} name={request.resolver.full_name} /></span>
                    )}
                  </div>
                </div>
              </div>

              {request.status !== "resolved" && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleResolve(request)}
                    disabled={updateRequest.isPending}
                  >
                    {updateRequest.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-1" />
                    )}
                    Resolve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    asChild
                  >
                    <Link to={`/courses/${request.course_id}`}>
                      View Course
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
